import { createAction } from "redux-actions";

import {
  REQUEST_CURRENT_USER_SUCCESS,
  REQUEST_CURRENT_USER_FAILURE,
  CREATE_USER_SUCCESS,
  CREATE_USER_FAILURE,
  UPDATE_USER_SUCCESS,
  UPDATE_USER_FAILURE,
  INVITE_USER_SUCCESS,
  INVITE_USER_FAILURE,
  GET_CURRENT_USER_FROM_LOCALSTORAGE,
  GET_INVITATIONS_SUCCESS,
  GET_INVITATIONS_FAILURE,
  RESEND_INVITATION_SUCCESS,
  RESEND_INVITATION_FAILURE,
  CANCEL_INVITATION_SUCCESS,
  CANCEL_INVITATION_FAILURE,
} from "../constants/actionTypes";
import * as User from "../api/user";

const requestCurrentUserSuccess = createAction(REQUEST_CURRENT_USER_SUCCESS);
const requestCurrentUserFailure = createAction(REQUEST_CURRENT_USER_FAILURE);

const PRIMARY_LOCATION_SESSION_KEY = "riverr.primaryLocationApplied";

/**
 * On first load of a signed-in browser session, snap active location to
 * primaryLocationId so sign-in lands on the warehouse chosen in Team access.
 * Mid-session sidebar switches are left alone until the next login.
 */
async function applyPrimaryLocationOnSignIn(payload) {
  const user = payload?.data?.user || payload?.user || {};
  const uid = user.uid;
  const primary = user.primaryLocationId;
  if (!uid || !primary) return payload;
  if (String(user.locationId || "") === String(primary)) return payload;

  const allowed = Array.isArray(user.locationIds)
    ? user.locationIds.map(String)
    : user.locationId
    ? [String(user.locationId)]
    : [];
  if (!allowed.includes(String(primary))) return payload;

  try {
    if (typeof sessionStorage === "undefined") return payload;
    const key = `${PRIMARY_LOCATION_SESSION_KEY}:${uid}`;
    if (sessionStorage.getItem(key) === "1") return payload;
    sessionStorage.setItem(key, "1");
    await User.switchActiveLocation(primary);
    return User.requestCurrentUser();
  } catch (_) {
    return payload;
  }
}

export function clearPrimaryLocationSession(uid) {
  try {
    if (typeof sessionStorage === "undefined") return;
    if (uid) {
      sessionStorage.removeItem(`${PRIMARY_LOCATION_SESSION_KEY}:${uid}`);
    }
  } catch (_) {
    /* ignore */
  }
}

export function requestCurrentUser() {
  return (dispatch) =>
    User.requestCurrentUser()
      .then((data) => applyPrimaryLocationOnSignIn(data))
      .then((data) => dispatch(requestCurrentUserSuccess(data)))
      .catch((error) => dispatch(requestCurrentUserFailure(error)));
}

const createUserSuccess = createAction(CREATE_USER_SUCCESS);
const createUserFailure = createAction(CREATE_USER_FAILURE);

export function createUser(params) {
  return (dispatch) =>
    User.createUser(params)
      .then((data) => dispatch(createUserSuccess(data)))
      .catch((error) => dispatch(createUserFailure(error)));
}

const updateUserSuccess = createAction(UPDATE_USER_SUCCESS);
const updateUserFailure = createAction(UPDATE_USER_FAILURE);

export function updateUser(params) {
  return (dispatch) =>
    User.updateUser(params)
      .then((data) => dispatch(updateUserSuccess(data)))
      .catch((error) => dispatch(updateUserFailure(error)));
}

const inviteUserSuccess = createAction(INVITE_USER_SUCCESS);
const inviteUserFailure = createAction(INVITE_USER_FAILURE);

export function inviteUser(params) {
  return (dispatch) =>
    User.inviteUser(params)
      .then((data) => dispatch(inviteUserSuccess(data)))
      .catch((error) => dispatch(inviteUserFailure(error)));
}

const getInvitationsSuccess = createAction(GET_INVITATIONS_SUCCESS);
const getInvitationsFailure = createAction(GET_INVITATIONS_FAILURE);

export function fetchInvitations() {
  return (dispatch) =>
    User.getInvitations()
      .then((data) => dispatch(getInvitationsSuccess(data)))
      .catch((error) => dispatch(getInvitationsFailure(error)));
}

const resendInvitationSuccess = createAction(RESEND_INVITATION_SUCCESS);
const resendInvitationFailure = createAction(RESEND_INVITATION_FAILURE);

export function resendInvitation(invitationId) {
  return (dispatch) =>
    User.resendInvitation(invitationId)
      .then((data) => dispatch(resendInvitationSuccess(data)))
      .catch((error) => dispatch(resendInvitationFailure(error)));
}

const cancelInvitationSuccess = createAction(CANCEL_INVITATION_SUCCESS);
const cancelInvitationFailure = createAction(CANCEL_INVITATION_FAILURE);

export function cancelInvitation(invitationId) {
  return (dispatch) =>
    User.cancelInvitation(invitationId)
      .then((data) => dispatch(cancelInvitationSuccess(data)))
      .catch((error) => dispatch(cancelInvitationFailure(error)));
}

export const getCurrentUserFromLocalstorage = createAction(
  GET_CURRENT_USER_FROM_LOCALSTORAGE
);
